import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../context/AuthContext';

const SHOP_CATEGORIES = [
  'General Store', 'Kirana / Grocery', 'Medical / Pharmacy', 'Clothing & Textiles',
  'Electronics', 'Hardware & Tools', 'Furniture', 'Jewellery', 'Agriculture Supplies',
  'Auto Parts', 'Stationery & Books', 'Dairy & Milk', 'Bakery & Food', 'Mobile Shop',
  'Petrol Pump', 'Hotel / Dhaba', 'Salon / Beauty', 'Footwear', 'Optical', 'Other',
];

const inp = {
  width: '100%', padding: '11px 14px', borderRadius: '10px', boxSizing: 'border-box',
  border: '1px solid #e2e8f0', fontSize: '14px', fontFamily: "'Sora', sans-serif",
  outline: 'none', color: '#1e293b', background: '#fff',
};
const lbl = { display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '6px', marginTop: '14px' };

export default function AdminRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState('');

  const [form, setForm] = useState({
    full_name: '', shop_name: '', shop_category: '', shop_category_custom: '',
    shop_registration_number: '', gstin: '',
    city: '', pincode: '', full_address: '', age: '', phone: '', email: '',
    password: '', confirm_password: '', aadhaar_number: '', pan_number: '',
  });
  const [fieldErrors, setFieldErrors] = React.useState({});

  const validateField = (k, v) => {
    let err = '';
    if (k === 'full_name') {
      if (!v) err = 'Full name is required';
      else if (!/^[a-zA-Z\s]+$/.test(v)) err = 'Name must contain letters only, no numbers';
      else if (v.length < 2) err = 'Name must be at least 2 characters';
    }
    if (k === 'shop_name') {
      if (!v) err = 'Shop name is required';
      else if (v.length < 2) err = 'Shop name too short';
    }
    if (k === 'city') {
      if (!v) err = 'City is required';
      else if (!/^[a-zA-Z\s]+$/.test(v)) err = 'City name must contain letters only, no numbers';
    }
    if (k === 'pincode') {
      if (!v) err = 'Pincode is required';
      else if (!/^\d{6}$/.test(v)) err = 'Pincode must be exactly 6 digits';
    }
    if (k === 'full_address') {
      if (!v) err = 'Address is required';
      else if (v.length < 10) err = 'Please enter a complete address';
    }
    if (k === 'age') {
      if (!v) err = 'Age is required';
      else if (isNaN(v) || parseInt(v) < 18) err = 'Age must be 18 or above';
      else if (parseInt(v) > 100) err = 'Please enter a valid age';
    }
    if (k === 'email') {
      if (!v) err = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) err = 'Please enter a valid email address';
    }
    if (k === 'phone') {
      if (!v) err = 'Phone is required';
      else if (!/^[6-9]\d{9}$/.test(v)) err = 'Phone must be 10 digits starting with 6, 7, 8 or 9';
    }
    if (k === 'aadhaar_number') {
      if (v && !/^\d{12}$/.test(v)) err = 'Aadhaar must be exactly 12 digits, numbers only';
    }
    if (k === 'pan_number') {
      if (v && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v.toUpperCase())) err = 'PAN format: 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F)';
    }
    if (k === 'shop_registration_number') {
      if (!v) err = 'Shop registration number is required';
      else if (v.length < 3) err = 'Please enter a valid registration number';
    }
    if (k === 'gstin') {
      if (!v) err = 'GSTIN is required';
      else if (v.length !== 15) err = 'GSTIN must be exactly 15 characters';
      else if (!/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/.test(v)) err = 'Invalid GSTIN format. e.g. 27AABCU9603R1ZX';
    }
    if (k === 'password') {
      if (!v) err = 'Password is required';
      else if (v.length < 6) err = 'Password must be at least 6 characters';
    }
    if (k === 'confirm_password') {
      if (!v) err = 'Please confirm your password';
    }
    return err;
  };

  const set = k => e => {
    const v = e.target.value;
    setForm(f => ({ ...f, [k]: v }));
    const err = validateField(k, v);
    setFieldErrors(prev => ({ ...prev, [k]: err }));
  };

  const errStyle = { fontSize: '11px', color: '#dc2626', marginTop: '3px', display: 'block' };
  const getInpStyle = (k) => ({
    ...inp,
    borderColor: fieldErrors[k] ? '#dc2626' : (form[k] && !fieldErrors[k] ? '#16a34a' : '#e2e8f0'),
    borderWidth: '1.5px'
  });

  const sendOTP = async () => {
    if (!form.phone || form.phone.length < 10) return toast.error('Enter valid 10-digit phone');
    try {
      await api.post('/auth/otp/send', { phone: form.phone, email: form.email || undefined, purpose: 'ADMIN_REGISTER' });
      setOtpSent(true);
      if (form.email) toast.success(`OTP sent to ${form.email} ✉️`, { duration: 6000 });
      else toast.success('OTP sent! Check your email.', { duration: 6000 });
    } catch (err) { toast.error('OTP failed: ' + (err.response?.data?.message || err.message)); }
  };

  const verifyOTP = async () => {
    try {
      await api.post('/auth/otp/verify', { phone: form.phone, otp, purpose: 'ADMIN_REGISTER' });
      setOtpVerified(true); toast.success('Phone verified! ✓');
    } catch { toast.error('Invalid OTP. Try resending.'); }
  };

  const [kycImages, setKycImages] = useState({ aadhaar_front: null, aadhaar_back: null, pan_card: null, shop_doc: null });
  const [kycPreviews, setKycPreviews] = useState({ aadhaar_front: null, aadhaar_back: null, pan_card: null, shop_doc: null });

  const handleImageChange = (field) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Image must be under 5MB');
    setKycImages(prev => ({ ...prev, [field]: file }));
    const reader = new FileReader();
    reader.onload = (ev) => setKycPreviews(prev => ({ ...prev, [field]: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    // Validate all fields before submit
    const requiredFields = ['full_name','age','email','phone','aadhaar_number','pan_number','password','confirm_password'];
    const newErrors = {};
    requiredFields.forEach(k => {
      const err = validateField(k, form[k] || '');
      if (err) newErrors[k] = err;
    });
    if (form.password !== form.confirm_password) newErrors.confirm_password = 'Passwords do not match';
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(prev => ({ ...prev, ...newErrors }));
      toast.error('Please fix all errors before submitting');
      return;
    }
    if (!otpVerified) return toast.error('Verify phone first');
    if (form.shop_category === 'Other' && !form.shop_category_custom)
      return toast.error('Please specify your shop category');
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, v));
      if (kycImages.aadhaar_front) formData.append('aadhaar_front', kycImages.aadhaar_front);
      if (kycImages.aadhaar_back)  formData.append('aadhaar_back',  kycImages.aadhaar_back);
      if (kycImages.pan_card)      formData.append('pan_card',      kycImages.pan_card);
      if (kycImages.shop_doc)      formData.append('shop_doc',      kycImages.shop_doc);
      await api.post('/auth/register/admin', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Registration submitted! Awaiting Super Admin approval.');
      navigate('/login');
    } catch (err) { toast.error(err.response?.data?.message || 'Registration failed'); }
    finally { setLoading(false); }
  };

  const page = { minHeight: '100vh', background: 'linear-gradient(135deg,#0f172a,#1e293b)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', fontFamily: "'Sora',sans-serif", padding: '2rem 1rem' };
  const card = { background: '#fff', borderRadius: '20px', padding: '2rem', width: '100%', maxWidth: '560px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };

  return (
    <div style={page}>
      <div style={card}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '52px', height: '52px', borderRadius: '14px', background: 'linear-gradient(135deg,#F97316,#EF4444)', fontSize: '22px', marginBottom: '10px' }}>₹</div>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Register as Lender</h1>
          <p style={{ color: '#94a3b8', fontSize: '13px', margin: '4px 0 0' }}>Join Udhari as a verified local lender</p>
        </div>

        {/* Steps */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
          {['Shop Info', 'Personal', 'Verify'].map((s, i) => (
            <React.Fragment key={i}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: step > i + 1 ? '#16a34a' : step === i + 1 ? '#F97316' : '#e2e8f0', color: step >= i + 1 ? '#fff' : '#94a3b8', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                <div style={{ fontSize: '10px', color: step === i + 1 ? '#F97316' : '#94a3b8', marginTop: '2px', fontWeight: '600' }}>{s}</div>
              </div>
              {i < 2 && <div style={{ height: '1px', flex: 0.5, background: step > i + 1 ? '#16a34a' : '#e2e8f0', marginBottom: '14px' }} />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: Shop Info */}
        {step === 1 && (
          <div>
            <label style={lbl}>Shop Name *</label>
            <input style={getInpStyle('shop_name')} placeholder="e.g. Sharma General Store" value={form.shop_name}
              onChange={e => { const v = e.target.value.replace(/[^a-zA-Z\s&.'\-]/g,''); setForm(f=>({...f,shop_name:v})); setFieldErrors(p=>({...p,shop_name:v.length<2?'Shop name must be at least 2 characters':''})); }} />
            {fieldErrors.shop_name && <span style={errStyle}>⚠ {fieldErrors.shop_name}</span>}
            {form.shop_name && !fieldErrors.shop_name && form.shop_name.length>=2 && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Valid</span>}

            <label style={lbl}>Shop Category *</label>
            <select style={inp} value={form.shop_category} onChange={set('shop_category')}>
              <option value="">Select category</option>
              {SHOP_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>

            {/* Show custom input if Other is selected */}
            {form.shop_category === 'Other' && (
              <div>
                <label style={{ ...lbl, color: '#F97316' }}>Specify Your Category *</label>
                <input
                  style={{ ...inp, borderColor: '#F97316', borderWidth: '2px' }}
                  placeholder="e.g. Cycle Repair, Fish Shop, Laundry..."
                  value={form.shop_category_custom}
                  onChange={set('shop_category_custom')}
                  autoFocus
                />
              </div>
            )}

            <label style={lbl}>Shop Registration No. *</label>
            <input style={{...inp, borderColor: fieldErrors.shop_registration_number ? '#dc2626' : form.shop_registration_number && !fieldErrors.shop_registration_number ? '#16a34a' : '#e2e8f0', borderWidth:'1.5px'}}
              placeholder="e.g. MH-NGP-2024-001"
              value={form.shop_registration_number}
              onChange={e => { const v = e.target.value; setForm(f=>({...f,shop_registration_number:v})); setFieldErrors(p=>({...p,shop_registration_number:validateField('shop_registration_number',v)})); }} />
            {fieldErrors.shop_registration_number && <span style={errStyle}>⚠ {fieldErrors.shop_registration_number}</span>}
            {form.shop_registration_number && !fieldErrors.shop_registration_number && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Valid format</span>}

            <label style={lbl}>GSTIN * <span style={{fontSize:"11px",color:"#64748b"}}>(Format: 27AABCU9603R1ZX)</span></label>
            <input
              style={{ ...inp, textTransform: 'uppercase', borderColor: fieldErrors.gstin ? '#dc2626' : form.gstin && !fieldErrors.gstin && form.gstin.length===15 ? '#16a34a' : '#e2e8f0', borderWidth:'1.5px' }}
              placeholder="e.g. 27AABCU9603R1ZX"
              maxLength={15}
              value={form.gstin}
              onChange={e => { const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,''); setForm(f=>({...f,gstin:v})); setFieldErrors(p=>({...p,gstin:validateField('gstin',v)})); }}
            />
            {fieldErrors.gstin && <span style={errStyle}>⚠ {fieldErrors.gstin}</span>}
            {form.gstin && !fieldErrors.gstin && form.gstin.length===15 && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Valid GSTIN</span>}

            <label style={lbl}>City *</label>
            <input style={getInpStyle('city')} placeholder="e.g. Nagpur" value={form.city}
              onChange={e => { const v = e.target.value.replace(/[^a-zA-Z\s]/g, ''); setForm(f=>({...f, city:v})); setFieldErrors(p=>({...p, city: validateField('city',v)})); }} />
            {fieldErrors.city && <span style={errStyle}>⚠ {fieldErrors.city}</span>}
            {form.city && !fieldErrors.city && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Valid</span>}

            <label style={lbl}>Pincode *</label>
            <input style={getInpStyle('pincode')} placeholder="6-digit pincode" maxLength={6} value={form.pincode}
              onChange={e => { const v = e.target.value.replace(/[^0-9]/g, ''); setForm(f=>({...f, pincode:v})); setFieldErrors(p=>({...p, pincode: validateField('pincode',v)})); }} />
            {fieldErrors.pincode && <span style={errStyle}>⚠ {fieldErrors.pincode}</span>}
            {form.pincode && !fieldErrors.pincode && form.pincode.length===6 && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Valid pincode</span>}

            <label style={lbl}>Full Address *</label>
            <textarea style={{ ...inp, height: '75px', resize: 'none' }} placeholder="Complete shop address..." value={form.full_address} onChange={set('full_address')} />

            <button
              style={{ marginTop: '1.5rem', width: '100%', padding: '13px', background: 'linear-gradient(135deg,#F97316,#EF4444)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Sora',sans-serif" }}
              onClick={() => {
                if (!form.shop_name || !form.shop_category || !form.city || !form.pincode || !form.full_address)
                  return toast.error('Fill all required fields');
                if (!form.shop_registration_number)
                  return toast.error('Shop Registration Number is required');
                if (!form.gstin || form.gstin.length !== 15)
                  return toast.error('Valid 15-digit GSTIN is required');
                if (form.shop_category === 'Other' && !form.shop_category_custom)
                  return toast.error('Please specify your shop category');
                setStep(2);
              }}
            >Continue →</button>
          </div>
        )}

        {/* STEP 2: Personal Info */}
        {step === 2 && (
          <div>
            <label style={lbl}>Full Name *</label>
            <input style={getInpStyle('full_name')} placeholder="Enter your name (letters only)" value={form.full_name}
              onChange={e => { const v = e.target.value.replace(/[^a-zA-Z\s]/g, ''); setForm(f=>({...f, full_name:v})); setFieldErrors(p=>({...p, full_name: validateField('full_name',v)})); }} />
            {fieldErrors.full_name && <span style={errStyle}>⚠ {fieldErrors.full_name}</span>}
            {form.full_name && !fieldErrors.full_name && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Valid</span>}

            <label style={lbl}>Age *</label>
            <input style={getInpStyle('age')} type="number" min={18} max={100} placeholder="Your age (must be 18+)" value={form.age}
              onChange={e => { const v = e.target.value; setForm(f=>({...f, age:v})); setFieldErrors(p=>({...p, age: validateField('age',v)})); }} />
            {fieldErrors.age && <span style={errStyle}>⚠ {fieldErrors.age}</span>}
            {form.age && !fieldErrors.age && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Valid age</span>}

            <label style={lbl}>Email *</label>
            <input style={getInpStyle('email')} type="email" placeholder="Your email address" value={form.email} onChange={set('email')} />
            {fieldErrors.email && <span style={errStyle}>⚠ {fieldErrors.email}</span>}
            {form.email && !fieldErrors.email && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Valid email</span>}

            <label style={lbl}>Aadhaar Number * <span style={{fontSize:"11px",color:"#dc2626"}}>(Mandatory)</span></label>
            <input style={getInpStyle('aadhaar_number')} placeholder="12-digit Aadhaar number (numbers only)" maxLength={12} value={form.aadhaar_number}
              onChange={e => { const v = e.target.value.replace(/[^0-9]/g,''); setForm(f=>({...f, aadhaar_number:v})); setFieldErrors(p=>({...p, aadhaar_number: validateField('aadhaar_number',v)})); }} />
            {fieldErrors.aadhaar_number && <span style={errStyle}>⚠ {fieldErrors.aadhaar_number}</span>}
            {form.aadhaar_number && !fieldErrors.aadhaar_number && form.aadhaar_number.length===12 && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Valid Aadhaar</span>}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                {[{ field: 'aadhaar_front', label: 'Aadhaar Front' }, { field: 'aadhaar_back', label: 'Aadhaar Back' }].map(({ field, label }) => (
                  <div key={field}>
                    <label style={{ ...lbl, marginTop: '6px' }}>{label}</label>
                    <label style={{ display: 'block', cursor: 'pointer' }}>
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange(field)} />
                      {kycPreviews[field] ? (
                        <div style={{ position: 'relative' }}>
                          <img src={kycPreviews[field]} alt={label} style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #16a34a' }} />
                          <div style={{ position: 'absolute', bottom: '4px', right: '4px', background: '#16a34a', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff' }}>✓</div>
                        </div>
                      ) : (
                        <div style={{ height: '80px', border: '2px dashed #cbd5e1', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', gap: '4px' }}>
                          <span style={{ fontSize: '20px' }}>📷</span>
                          <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>Upload Photo</span>
                        </div>
                      )}
                    </label>
                  </div>
                ))}
              </div>

            <label style={lbl}>PAN Number * <span style={{fontSize:"11px",color:"#dc2626"}}>(Mandatory)</span></label>
            <input style={{...getInpStyle('pan_number'), textTransform:'uppercase'}} placeholder="e.g. ABCDE1234F" maxLength={10} value={form.pan_number}
              onChange={e => { const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,''); setForm(f=>({...f, pan_number:v})); setFieldErrors(p=>({...p, pan_number: validateField('pan_number',v)})); }} />
            {fieldErrors.pan_number && <span style={errStyle}>⚠ {fieldErrors.pan_number}</span>}
            {form.pan_number && !fieldErrors.pan_number && form.pan_number.length===10 && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Valid PAN</span>}

            <div style={{ marginBottom: '12px' }}>
                <label style={{ ...lbl, marginTop: '6px' }}>PAN Card Photo</label>
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange('pan_card')} />
                  {kycPreviews.pan_card ? (
                    <div style={{ position: 'relative' }}>
                      <img src={kycPreviews.pan_card} alt="PAN" style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #16a34a' }} />
                      <div style={{ position: 'absolute', bottom: '4px', right: '4px', background: '#16a34a', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff' }}>✓</div>
                    </div>
                  ) : (
                    <div style={{ height: '70px', border: '2px dashed #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>📷</span>
                      <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>Upload PAN Card Photo</span>
                    </div>
                  )}
                </label>
            </div>

            <div style={{ marginBottom: '12px' }}>
                <label style={{ ...lbl, marginTop: '6px' }}>Shop Document * <span style={{fontSize:'11px',color:'#64748b'}}>(Registration cert, license, or shop proof)</span></label>
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleImageChange('shop_doc')} />
                  {kycPreviews.shop_doc ? (
                    <div style={{ position: 'relative' }}>
                      <img src={kycPreviews.shop_doc} alt="Shop Doc" style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #16a34a' }} />
                      <div style={{ position: 'absolute', bottom: '4px', right: '4px', background: '#16a34a', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff' }}>✓</div>
                    </div>
                  ) : (
                    <div style={{ height: '70px', border: '2px dashed #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>🏪</span>
                      <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600' }}>Upload Shop Document</span>
                    </div>
                  )}
                </label>
            </div>

            <label style={lbl}>Password *</label>
            <input style={getInpStyle('password')} type="password" placeholder="Min 6 characters" value={form.password} onChange={set('password')} />
            {fieldErrors.password && <span style={errStyle}>⚠ {fieldErrors.password}</span>}

            <label style={lbl}>Confirm Password *</label>
            <input style={{...inp, borderColor: form.confirm_password && form.confirm_password!==form.password ? '#dc2626' : form.confirm_password && form.confirm_password===form.password ? '#16a34a' : '#e2e8f0', borderWidth:'1.5px'}} type="password" placeholder="Repeat password" value={form.confirm_password} onChange={set('confirm_password')} />
            {form.confirm_password && form.confirm_password !== form.password && <span style={errStyle}>⚠ Passwords do not match</span>}
            {form.confirm_password && form.confirm_password === form.password && <span style={{fontSize:'11px',color:'#16a34a',marginTop:'3px',display:'block'}}>✓ Passwords match</span>}

            <div style={{ display: 'flex', gap: '10px', marginTop: '1.5rem' }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, padding: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Sora',sans-serif", color: '#374151' }}>← Back</button>
              <button onClick={() => {
                if (!form.full_name || !form.age || !form.email || !form.password) return toast.error('Fill all required fields');
                if (!form.aadhaar_number || form.aadhaar_number.length !== 12) return toast.error('Valid 12-digit Aadhaar is required');
                if (!form.pan_number || form.pan_number.length !== 10) return toast.error('Valid 10-character PAN is required');
                if (form.password !== form.confirm_password) return toast.error('Passwords do not match');
                setStep(3);
              }} style={{ flex: 2, padding: '12px', background: 'linear-gradient(135deg,#F97316,#EF4444)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Sora',sans-serif" }}>Continue →</button>
            </div>
          </div>
        )}

        {/* STEP 3: Phone Verify */}
        {step === 3 && (
          <div>
            <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '12px 16px', marginBottom: '1.5rem', border: '1px solid #bfdbfe' }}>
              <p style={{ fontSize: '13px', color: '#1d4ed8', margin: 0 }}>📱 Verify phone to complete. Account will be reviewed by Super Admin.</p>
              <p style={{ fontSize: '12px', color: '#1d4ed8', margin: '6px 0 0 0' }}>📧 OTP will be sent to your registered email ID.</p>
            </div>

            <label style={lbl}>Phone Number *</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input style={{ ...inp, flex: 1, borderColor: fieldErrors.phone ? '#dc2626' : form.phone && !fieldErrors.phone ? '#16a34a' : '#e2e8f0', borderWidth:'1.5px' }}
                type="tel" placeholder="10-digit mobile (e.g. 9876543210)" maxLength={10} value={form.phone}
                onChange={e => { const v = e.target.value.replace(/[^0-9]/g,''); setForm(f=>({...f,phone:v})); setFieldErrors(p=>({...p,phone:validateField('phone',v)})); }}
                disabled={otpVerified} />
              {!otpVerified && (
                <button onClick={sendOTP} style={{ padding: '11px 16px', borderRadius: '10px', background: '#F97316', color: '#fff', border: 'none', fontWeight: '600', cursor: 'pointer', fontFamily: "'Sora',sans-serif", fontSize: '13px', whiteSpace: 'nowrap' }}>
                  {otpSent ? 'Resend' : 'Send OTP'}
                </button>
              )}
            </div>

            {otpSent && !otpVerified && (
              <div>
                {devOtp && (
                  <div style={{ marginTop: '10px', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px' }}>
                    <div style={{ fontSize: '12px', color: '#15803d', fontWeight: '600' }}>🔐 Your OTP (Dev Mode):</div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#15803d', letterSpacing: '0.3em' }}>{devOtp}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>Enter this below ↓</div>
                  </div>
                )}
                <label style={lbl}>Enter OTP *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input style={{ ...inp, flex: 1, letterSpacing: '0.3em', fontSize: '20px', textAlign: 'center' }} placeholder="• • • • • •" maxLength={6} value={otp} onChange={e => setOtp(e.target.value)} />
                  <button onClick={verifyOTP} style={{ padding: '11px 16px', borderRadius: '10px', background: '#16a34a', color: '#fff', border: 'none', fontWeight: '600', cursor: 'pointer', fontFamily: "'Sora',sans-serif", fontSize: '13px' }}>Verify ✓</button>
                </div>
              </div>
            )}

            {otpVerified && (
              <div style={{ marginTop: '10px', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>✅</span>
                <div><div style={{ fontWeight: '700', color: '#15803d' }}>Phone Verified!</div><div style={{ fontSize: '12px', color: '#64748b' }}>{form.phone}</div></div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '1.5rem' }}>
              <button onClick={() => setStep(2)} style={{ flex: 1, padding: '13px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Sora',sans-serif", color: '#374151' }}>← Back</button>
              <button onClick={submit} disabled={!otpVerified || loading}
                style={{ flex: 2, padding: '13px', background: otpVerified && !loading ? 'linear-gradient(135deg,#F97316,#EF4444)' : '#e2e8f0', color: otpVerified ? '#fff' : '#94a3b8', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '700', cursor: otpVerified ? 'pointer' : 'not-allowed', fontFamily: "'Sora',sans-serif" }}>
                {loading ? 'Submitting...' : 'Submit Registration ✓'}
              </button>
            </div>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #f1f5f9' }}>
          <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
            Already registered? <Link to="/login" style={{ color: '#F97316', textDecoration: 'none', fontWeight: '600' }}>Sign In →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
